import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE from "../config";

const ViewDemand = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const demand = location.state?.demand;

    const [partPayments, setPartPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!demand) {
            navigate("/dashboard/demands");
            return;
        }

        setLoading(true);
        fetch(`${API_BASE}/api/allrows`, {
            method: "POST",
            headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "demand_payment" })
        })
            .then(res => res.json())
            .then(json => {
                setLoading(false);
                if (Array.isArray(json.data)) {
                    // Filter part payments matching the demand_id
                    const paymentsForThisDemand = json.data.filter(dp => dp.demand === demand.demand_id);
                    setPartPayments(paymentsForThisDemand);
                } else {
                    setError("Failed to load part payments.");
                }
            })
            .catch(() => {
                setLoading(false);
                setError("Error fetching part payments.");
            });
    }, [demand, navigate]);

    if (!demand) return null;

    const fmt = (v) => {
        const n = Number(v);
        return isNaN(n) || n === 0 ? "—" : "₹" + n.toLocaleString("en-IN");
    };

    return (
        <div className="vd-page">
            <style>{`
        .vd-page { max-width: 1000px; margin: 0 auto; padding-bottom: 40px; }
        .vd-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .vd-back-btn { background: none; border: none; color: #64748b; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .vd-back-btn:hover { color: #0f172a; }
        .vd-title { font-size: 24px; font-weight: 700; color: #0f172a; }
        
        /* Overview Card */
        .vd-card { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.03); overflow: hidden; margin-bottom: 32px; }
        .vd-card-head { padding: 20px 24px; background: linear-gradient(to right, #f8fafc, #f1f5f9); border-bottom: 1px solid #e2e8f0; display:flex; justify-content:space-between; align-items:center;}
        .vd-card-title { font-size: 18px; font-weight: 700; color: #0f172a; }
        .vd-badge-done { background:#ecfdf5; color:#059669; padding:6px 14px; border-radius:20px; font-weight:700; font-size:12px; border:1px solid #a7f3d0; }
        .vd-badge-due { background:#fffbeb; color:#d97706; padding:6px 14px; border-radius:20px; font-weight:700; font-size:12px; border:1px solid #fcd34d; }
        .vd-badge-na { background:#f8fafc; color:#94a3b8; padding:6px 14px; border-radius:20px; font-weight:700; font-size:12px; border:1px solid #e2e8f0; }

        .vd-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px; padding: 24px; }
        .vd-item { display: flex; flex-direction: column; gap: 4px; }
        .vd-lbl { font-size: 12px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .vd-val { font-size: 15px; font-weight: 600; color: #0f172a; word-wrap: break-word; }
        
        /* Part Payments Table */
        .vd-sec-title { font-size: 18px; font-weight: 700; color: #0f172a; margin-bottom: 16px; display:flex; align-items:center; gap:8px;}
        .vd-table-wrap { background: #fff; border-radius: 14px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.04); overflow-x: auto; }
        .vd-table { width: 100%; border-collapse: collapse; min-width: 600px; }
        .vd-table th, .vd-table td { padding: 14px 16px; font-size: 14px; text-align: left; border-bottom: 1px solid #f1f5f9; }
        .vd-table th { background: #f8fafc; color: #64748b; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
        .vd-table tbody tr:last-child td { border-bottom: none; }
        .vd-table tbody tr:hover { background: #f8fafc; }
        
        .vd-spin { width:24px; height:24px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:vd-sp .7s linear infinite; margin: 20px auto; }
        @keyframes vd-sp { to{transform:rotate(360deg)} }
        
        .vd-empty { padding: 40px 20px; text-align: center; color: #94a3b8; font-size: 14px; }
        .vd-err { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; color: #dc2626; font-size: 14px; text-align: center; margin-bottom: 20px;}
      `}</style>

            <div className="vd-header">
                <button className="vd-back-btn" onClick={() => navigate(-1)}>
                    ← Back to Demands
                </button>
                <div className="vd-title">Demand Record</div>
                <div style={{ width: 140 }}></div>
            </div>

            <div className="vd-card">
                <div className="vd-card-head">
                    <div className="vd-card-title">Demand Overview</div>
                    <span className={demand.status === "Payment Done" ? "vd-badge-done" : demand.status === "Due" ? "vd-badge-due" : "vd-badge-na"}>
                        {demand.status || "Unknown Status"}
                    </span>
                </div>
                <div className="vd-grid">
                    <div className="vd-item">
                        <span className="vd-lbl">Demand ID</span>
                        <span className="vd-val">{demand.demand_id}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Customer Name</span>
                        <span className="vd-val">{demand.customer_name}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Booking ID</span>
                        <span className="vd-val">{demand.booking_id}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Unit Details</span>
                        <span className="vd-val">{demand.unit}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Stage Name</span>
                        <span className="vd-val">{demand.stage_name || "—"}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Total Given Amount</span>
                        <span className="vd-val" style={{ color: "#2563eb" }}>{fmt(demand.amount)}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Date of Demand</span>
                        <span className="vd-val">{demand.demand_date || "—"}</span>
                    </div>
                    <div className="vd-item">
                        <span className="vd-lbl">Due Date</span>
                        <span className="vd-val">{demand.due_date || "—"}</span>
                    </div>
                </div>
            </div>

            <div className="vd-sec-title">🔗 Part Payments History</div>
            {error && <div className="vd-err">⚠️ {error}</div>}
            
            <div className="vd-table-wrap">
                <table className="vd-table">
                    <thead>
                        <tr>
                            <th>Payment ID</th>
                            <th>Demand Ref</th>
                            <th>Date</th>
                            <th>Paid Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4"><div className="vd-spin" /></td></tr>
                        ) : partPayments.length === 0 ? (
                            <tr><td colSpan="4"><div className="vd-empty">No part payments recorded for this demand.</div></td></tr>
                        ) : (
                            partPayments.map(dp => (
                                <tr key={dp.id}>
                                    <td style={{ fontWeight: 600 }}>{dp.payment_id || "—"}</td>
                                    <td style={{ color: "#64748b" }}>{dp.demand || "—"}</td>
                                    <td>{dp.date || "—"}</td>
                                    <td style={{ fontWeight: 700, color: "#0f172a" }}>{fmt(dp.pay_amount)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ViewDemand;
